import { springs } from "@/components/ui/motion";
import type { AuditDashboardMetrics } from "@/packages/types";
import { motion } from "framer-motion";
import { Check, Shield } from "lucide-react";
import { WidgetCard, WidgetCardSkeleton } from "./WidgetCard";

interface FindingsCardProps {
  findingStats?: AuditDashboardMetrics["findingStats"];
  onNavigateToFindings?: () => void;
  index?: number;
}

const severityColors = {
  critical: "#f87171",
  high: "#fb923c",
  medium: "#fbbf24",
  low: "#60a5fa",
} as const;

/** Map schema severity to display severity */
const severityDisplayMap: Record<
  string,
  { key: keyof typeof severityColors; label: string }
> = {
  critical: { key: "critical", label: "Critical" },
  major: { key: "high", label: "Major" },
  minor: { key: "medium", label: "Minor" },
  observation: { key: "low", label: "Info" },
};

type SeverityKey = keyof typeof severityColors;

export function FindingsCard({
  findingStats,
  onNavigateToFindings,
  index,
}: FindingsCardProps) {
  if (!findingStats || findingStats.total === 0) {
    return (
      <WidgetCard
        size="S"
        title="Findings"
        icon={<Shield size={16} />}
        index={index}
      >
        <motion.div
          className="flex flex-col items-center justify-center h-full gap-1.5"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
        >
          <div className="w-16 h-16 rounded-full border-2 border-dashed border-border/60 flex items-center justify-center">
            <Shield size={28} className="text-muted" />
          </div>
          <span className="text-sm text-muted mt-1">No findings yet</span>
        </motion.div>
      </WidgetCard>
    );
  }

  const segments: { key: SeverityKey; count: number; color: string }[] = (
    ["critical", "high", "medium", "low"] as const
  )
    .map((key) => ({
      key,
      count: findingStats[key],
      color: severityColors[key],
    }))
    .filter((s) => s.count > 0);

  const total = segments.reduce((sum, s) => sum + s.count, 0);

  let runningOffset = 0;
  const segmentsWithOffset = segments.map((seg) => {
    const width = (seg.count / total) * 100;
    const offset = runningOffset;
    runningOffset += width;
    return { ...seg, width, offset };
  });

  const resolvedCount = findingStats.resolved;
  const resolvedPct = total > 0 ? Math.round((resolvedCount / total) * 100) : 0;

  const severityOrder: SeverityKey[] = ["critical", "high", "medium", "low"];
  const legendItems = severityOrder
    .filter((key) => findingStats[key] > 0)
    .map((key) => {
      const entry = Object.values(severityDisplayMap).find(
        (d) => d.key === key,
      );
      return {
        key,
        label: entry?.label ?? key,
        color: severityColors[key],
        count: findingStats[key],
      };
    });

  return (
    <WidgetCard
      size="S"
      title="Findings"
      icon={<Shield size={16} />}
      index={index}
    >
      <div className="flex flex-col h-full">
        {/* Header row */}
        <div className="flex items-baseline justify-between mb-1">
          <span className="text-lg font-semibold tabular-nums">
            {findingStats.total} Findings
          </span>
          {resolvedCount > 0 && (
            <span className="text-xs text-muted flex items-center gap-0.5">
              {resolvedCount} Resolved
              <Check size={12} className="text-emerald-400" />
            </span>
          )}
        </div>

        {/* Thick severity bar */}
        <svg
          width="100%"
          height="24"
          className="rounded-md overflow-hidden flex-shrink-0"
          role="img"
          aria-label="Findings severity distribution"
        >
          <defs>
            <linearGradient id="glass-overlay" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="white" stopOpacity="0.25" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </linearGradient>
          </defs>
          {segmentsWithOffset.map((seg, i) => (
            <g key={seg.key}>
              <motion.rect
                x={`${seg.offset}%`}
                y={0}
                height={24}
                fill={seg.color}
                rx={i === 0 || i === segmentsWithOffset.length - 1 ? 6 : 0}
                initial={{ width: 0 }}
                animate={{ width: `${seg.width}%` }}
                transition={{ ...springs.smooth, delay: 0.2 + i * 0.05 }}
              />
              <motion.rect
                x={`${seg.offset}%`}
                y={0}
                height={12}
                fill="url(#glass-overlay)"
                rx={i === 0 || i === segmentsWithOffset.length - 1 ? 6 : 0}
                initial={{ width: 0 }}
                animate={{ width: `${seg.width}%` }}
                transition={{ ...springs.smooth, delay: 0.2 + i * 0.05 }}
              />
            </g>
          ))}
        </svg>

        {/* Severity legend */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
          {legendItems.map((item) => (
            <div key={item.key} className="flex items-center gap-1">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-xs text-muted tabular-nums">
                {item.count} {item.label}
              </span>
            </div>
          ))}
        </div>

        {/* Resolved progress bar */}
        {resolvedCount > 0 && (
          <div className="mt-2 flex items-center gap-2">
            <div className="flex-1 h-3.5 rounded-full bg-border/50 overflow-hidden">
              <motion.div
                className="h-full rounded-full"
                style={{ backgroundColor: "#4ade80" }}
                initial={{ width: 0 }}
                animate={{ width: `${resolvedPct}%` }}
                transition={{ ...springs.smooth, delay: 0.4 }}
              />
            </div>
            <span className="text-xs text-muted tabular-nums whitespace-nowrap">
              {resolvedPct}% resolved
            </span>
          </div>
        )}

        {/* View all link */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onNavigateToFindings?.();
          }}
          className="text-[11px] text-accent hover:text-accent/80 transition-colors mt-auto pt-1 text-left"
        >
          View all findings →
        </button>
      </div>
    </WidgetCard>
  );
}

export function FindingsCardSkeleton({ index }: { index?: number }) {
  return <WidgetCardSkeleton size="S" index={index} />;
}
